import { useEffect, useState } from 'react';
import { sameSpokenLine } from '@vagusx/clients';
import {
  useWatch,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormRegisterReturn,
  type UseFormReturn,
} from 'react-hook-form';

export interface FollowedSuggestion<T extends FieldValues> {
  /** Spread onto the input. */
  field: UseFormRegisterReturn<Path<T>>;
  /** What the box holds right now, saved or not — what its play button speaks (ADR-344). */
  value: string;
  /** True when someone typed a line that is not the suggestion — show a way back to it. */
  diverged: boolean;
  /** True when the field holds the suggestion (following it, or typed to match it) — what saves as Auto. */
  auto: boolean;
  /** Put the suggestion back and follow it again. */
  follow: () => void;
}

/**
 * A text field that FOLLOWS a suggestion until someone types in it (ADR-342):
 * the spoken name follows the chart name and role, the spoken address follows
 * the address. While following, the field is kept equal to `suggestion` as the
 * fields it is built from change. Typing stops that; emptying the box starts it
 * again. On blur the typed text goes through `expand` (abbreviations written
 * out).
 */
export function useFollowedSuggestion<T extends FieldValues>({
  form,
  name,
  suggestion,
  expand,
  initiallyFollows,
}: {
  form: UseFormReturn<T>;
  name: Path<T>;
  suggestion: string;
  expand: (typed: string) => string;
  initiallyFollows: boolean;
}): FollowedSuggestion<T> {
  const [follows, setFollows] = useState(initiallyFollows);
  const value = (useWatch({ control: form.control, name }) as string | undefined) ?? '';

  useEffect(() => {
    if (follows && form.getValues(name) !== suggestion) {
      // DIRTY, so going back to the suggestion ("Use …") can be saved. A form
      // whose stored value already is the suggestion never reaches this line.
      form.setValue(name, suggestion as PathValue<T, Path<T>>, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [follows, suggestion, form, name]);

  const field = form.register(name, {
    onChange: () => setFollows(false),
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      const expanded = expand(e.target.value);
      if (expanded === '') setFollows(true);
      else if (expanded !== e.target.value) {
        form.setValue(name, expanded as PathValue<T, Path<T>>, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
  });

  // The API's own test for "is this the suggestion?", so the badge here, the
  // detail sheet's and what gets stored agree.
  const diverged = !follows && suggestion !== '' && !sameSpokenLine(value, suggestion);

  return { field, value, diverged, auto: !diverged, follow: () => setFollows(true) };
}
